import React, { useState, useEffect } from 'react';
import LocationSearch from '../LocationSearch/LocationSearch';
import { LegalModal, LegalModalType } from '../LegalModal/LegalModal';
import { pricesApi } from '@/services/api';
import type { Location } from '@/types';

// ── Icons ─────────────────────────────────────────────────────────────────────

const ArrowRight = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CrosshairIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/>
    <line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/>
    <line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>
  </svg>
);

const FlameIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5c.8 1.6 2.4 2.6 2.4 4.6 0 1-.6 1.6-1.2 1.8.4-1.2-.2-2.2-1-2.8 0 1.4-.8 1.8-1.6 2.4-1 .8-1.6 2-1.6 3.4 0 2 1.4 3.6 3 3.6s3-1.6 3-3.6c0-2.7-1.5-4.4-3-9.4z" fill="#1a1d24"/>
  </svg>
);


// ── Logo ──────────────────────────────────────────────────────────────────────

function Logo({ inverted = false }: { inverted?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: 'var(--amber)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 1px 0 rgba(255,255,255,0.18) inset, 0 4px 14px rgba(245,165,36,0.35)',
      }}>
        <FlameIcon size={18} />
      </div>
      <span style={{
        fontWeight: 700, fontSize: 18,
        letterSpacing: '-0.02em',
        color: inverted ? '#1a1d24' : 'var(--fg)',
      }}>FuelHunt</span>
    </div>
  );
}

// ── Eyebrow label ─────────────────────────────────────────────────────────────

function Eyebrow({ children, color = 'var(--amber)' }: { children: React.ReactNode; color?: string }) {
  return (
    <div className="mono" style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      fontSize: 11.5, color, letterSpacing: '0.14em', textTransform: 'uppercase',
    }}>
      <span style={{ width: 18, height: 1, background: color, flexShrink: 0 }}/>
      {children}
    </div>
  );
}

// ── Live results preview panel ─────────────────────────────────────────────────

const PREVIEW_STATIONS = [
  { brand: 'Costco',       price: 1.519, total: 91.14, dist: 4.2, drive: 9,  area: 'London E6',  saving: 3.40, color: '#005DAA' },
  { brand: 'Asda',         price: 1.609, total: 91.78, dist: 1.8, drive: 6,  area: 'London SE1', saving: 2.76, color: '#7BB142' },
  { brand: "Sainsbury's",  price: 1.619, total: 92.05, dist: 2.1, drive: 7,  area: 'London E1',  saving: 2.49, color: '#F06C00' },
  { brand: 'Tesco',        price: 1.629, total: 92.34, dist: 1.4, drive: 5,  area: 'London E2',  saving: 2.20, color: '#005EB8' },
  { brand: 'BP',           price: 1.649, total: 93.42, dist: 0.9, drive: 4,  area: 'London E14', saving: 1.12, color: '#009639' },
];

function LiveResultsPanel() {
  return (
    <div style={{
      background: 'linear-gradient(180deg, var(--ink-2-solid) 0%, var(--ink-1) 100%)',
      border: '1px solid var(--line)',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.05) inset',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid var(--line-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--fg-dim)', letterSpacing: '0.14em' }}>EXAMPLE · LONDON E1 · 50L DIESEL</div>
          <div style={{ fontSize: 14, color: 'var(--fg)', marginTop: 4, fontWeight: 500 }}>Cheapest by total cost</div>
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999,
          background: 'var(--green-soft)', border: '1px solid rgba(34,211,154,0.19)',
          fontSize: 11, color: 'var(--green)',
        }}>
          <span className="pulse-dot" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--green)' }}/>
          LIVE
        </div>
      </div>

      {/* Mini map strip */}
      <div style={{
        height: 100, position: 'relative',
        borderBottom: '1px solid var(--line-soft)',
        background: '#0e1421',
        backgroundImage: 'radial-gradient(circle at 20% 30%, #152136 0, transparent 40%), radial-gradient(circle at 70% 60%, #0d1828 0, transparent 35%), linear-gradient(45deg, transparent 48%, rgba(255,255,255,0.02) 49%, rgba(255,255,255,0.02) 51%, transparent 52%), linear-gradient(-45deg, transparent 48%, rgba(255,255,255,0.02) 49%, rgba(255,255,255,0.02) 51%, transparent 52%)',
        backgroundSize: '100% 100%, 100% 100%, 60px 60px, 60px 60px',
      }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 400 100" preserveAspectRatio="none">
          <path d="M50 70 Q 130 25, 200 50 T 360 45" stroke="#f5a524" strokeWidth="1.5" strokeDasharray="3 4" fill="none" opacity="0.5"/>
        </svg>
        {[
          { x: '12%', y: '68%', color: 'var(--green)',  label: '£1.519' },
          { x: '32%', y: '28%', color: 'var(--amber)',  label: '£1.609' },
          { x: '52%', y: '52%', color: 'var(--amber)',  label: '£1.619' },
          { x: '76%', y: '38%', color: '#6ea8ff',       label: '£1.629' },
          { x: '88%', y: '58%', color: '#6ea8ff',       label: '£1.649' },
        ].map((p, i) => (
          <div key={i} style={{
            position: 'absolute', left: p.x, top: p.y,
            transform: 'translate(-50%, -50%)',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color, boxShadow: `0 0 0 3px ${p.color}33` }}/>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg)', background: 'rgba(7,9,15,0.85)', padding: '2px 5px', borderRadius: 4, border: '1px solid var(--line)', whiteSpace: 'nowrap' }}>{p.label}</span>
          </div>
        ))}
      </div>

      {/* Station rows */}
      {PREVIEW_STATIONS.map((s, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 14,
          padding: '13px 20px',
          borderBottom: i < PREVIEW_STATIONS.length - 1 ? '1px solid var(--line-soft)' : 'none',
          alignItems: 'center',
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: 7,
            background: i === 0 ? 'var(--amber)' : 'var(--ink-3)',
            border: i === 0 ? 'none' : '1px solid var(--line)',
            color: i === 0 ? '#1a1d24' : 'var(--fg-mute)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)',
          }}>
            {i + 1}
          </div>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg)' }}>
              {s.brand} <span style={{ color: 'var(--fg-dim)', fontWeight: 400 }}>· {s.area}</span>
            </div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 2 }}>
              {s.dist} mi · {s.drive} min
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono" style={{ fontSize: 15, fontWeight: 600, color: i === 0 ? 'var(--green)' : 'var(--fg)' }}>
              £{s.total.toFixed(2)}
            </div>
            <div className="mono" style={{ fontSize: 11, color: i === 0 ? 'var(--green)' : 'var(--fg-dim)', marginTop: 1 }}>
              {i === 0 ? `Save £${s.saving.toFixed(2)}` : `${s.price.toFixed(3)}/L`}
            </div>
          </div>
        </div>
      ))}

      <div style={{
        padding: '11px 20px', background: 'var(--ink-0)',
        borderTop: '1px solid var(--line-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11.5, color: 'var(--fg-dim)',
      }}>
        <span>Sorted by total cost · fuel + drive</span>
        <span style={{ color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          See full results <ArrowRight size={11}/>
        </span>
      </div>
    </div>
  );
}

// ── Receipt comparison ─────────────────────────────────────────────────────────

function ReceiptRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--fg-mute)' }}>{label}</span>
      <span className="mono" style={{ color: 'var(--fg)' }}>{value}</span>
    </div>
  );
}

function Receipt({ title, rows, total, totalColor }: {
  title: string;
  rows: [string, string][];
  total: string;
  totalColor: string;
}) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: 'var(--fg)' }}>{title}</div>
      <div style={{ borderTop: '1px dashed var(--line-strong)', borderBottom: '1px dashed var(--line-strong)', padding: '8px 0', margin: '0 0 12px' }}>
        {rows.map(([l, v], i) => <ReceiptRow key={i} label={l} value={v} />)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 12, color: 'var(--fg-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total</span>
        <span className="mono" style={{ fontSize: 24, fontWeight: 600, color: totalColor, letterSpacing: '-0.01em' }}>{total}</span>
      </div>
    </div>
  );
}

function CompareReceipt() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 16 }}>
      <div style={{
        background: 'var(--ink-1)', border: '1px solid var(--line-soft)',
        borderRadius: 12, padding: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-dim)', letterSpacing: '0.1em' }}>OTHER APPS</span>
          <span style={{ fontSize: 11, color: 'var(--red)', padding: '2px 8px', border: '1px solid rgba(239,80,96,0.25)', borderRadius: 4 }}>£1.04 more</span>
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--fg-mute)', marginBottom: 16 }}>Sort by pump price ↓</div>
        <Receipt
          title="Asda · Old Kent Rd"
          rows={[
            ['Pump price', '£1.609/L'],
            ['50L fill', '£80.45'],
            ['Drive (8.4 mi · return)', '£1.85'],
          ]}
          total="£82.30"
          totalColor="var(--fg)"
        />
      </div>

      <div style={{
        background: 'linear-gradient(180deg, #0d1f1a, #0a1611)',
        border: '1px solid rgba(34,211,154,0.19)',
        borderRadius: 12, padding: 24,
        boxShadow: '0 12px 32px rgba(34,211,154,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--green)', letterSpacing: '0.1em' }}>FUELHUNT</span>
          <span style={{ fontSize: 11, color: 'var(--green)', padding: '2px 8px', background: 'var(--green-soft)', border: '1px solid rgba(34,211,154,0.25)', borderRadius: 4 }}>Cheapest</span>
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--fg-mute)', marginBottom: 16 }}>Sort by total cost ↓</div>
        <Receipt
          title="BP · Commercial Road"
          rows={[
            ['Pump price', '£1.619/L'],
            ['50L fill', '£80.95'],
            ['Drive (1.4 mi · return)', '£0.31'],
          ]}
          total="£81.26"
          totalColor="var(--green)"
        />
      </div>
    </div>
  );
}

// ── Price ticker ──────────────────────────────────────────────────────────────

const TICKER_CACHE_KEY = 'fh_city_prices_v2';
const TICKER_CACHE_TTL = 60 * 60 * 1000; // 1 hour in ms

// Fixed city config — one entry per major UK city, alternating fuel types for variety
const CITY_CONFIG = [
  { city: 'London',     area: 'E1',  postcode: 'E',  fuel_code: 'B7'  },
  { city: 'Manchester', area: 'M1',  postcode: 'M',  fuel_code: 'E10' },
  { city: 'Birmingham', area: 'B1',  postcode: 'B',  fuel_code: 'B7'  },
  { city: 'Leeds',      area: 'LS1', postcode: 'LS', fuel_code: 'E10' },
  { city: 'Glasgow',    area: 'G1',  postcode: 'G',  fuel_code: 'B7'  },
  { city: 'Cardiff',    area: 'CF1', postcode: 'CF', fuel_code: 'E10' },
  { city: 'Bristol',    area: 'BS1', postcode: 'BS', fuel_code: 'B7'  },
  { city: 'Liverpool',  area: 'L1',  postcode: 'L',  fuel_code: 'E10' },
];

interface TickerRow {
  city: string;
  area: string;
  fuelName: string;
  brand: string;
  pricePence: number;
}

interface TickerCache {
  data: TickerRow[];
  ts: number;
}

function readTickerCache(): TickerRow[] | null {
  try {
    const raw = localStorage.getItem(TICKER_CACHE_KEY);
    if (!raw) return null;
    const cached: TickerCache = JSON.parse(raw);
    if (Date.now() - cached.ts > TICKER_CACHE_TTL) return null;
    return cached.data;
  } catch {
    return null;
  }
}

function writeTickerCache(data: TickerRow[]) {
  try {
    localStorage.setItem(TICKER_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // localStorage unavailable — silently skip
  }
}

function PriceTicker() {
  const [rows, setRows] = useState<TickerRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = readTickerCache();
    if (cached) {
      setRows(cached);
      setLoading(false);
      return;
    }
    // One query per city, scoped to that city's postcode prefix → ensures geographic variety
    Promise.all(
      CITY_CONFIG.map(c =>
        pricesApi.getCheapest({ fuel_code: c.fuel_code, postcode: c.postcode, limit: 1 })
          .then(results => {
            const r = results[0];
            if (!r || r.price_pence == null) return null;
            const row: TickerRow = {
              city: c.city,
              area: c.area,
              fuelName: r.fuel_name,
              brand: r.brand,
              pricePence: r.price_pence,
            };
            return row;
          })
          .catch(() => null)
      )
    ).then(results => {
      const valid = results.filter((r): r is TickerRow => r !== null);
      writeTickerCache(valid);
      setRows(valid);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: 'var(--ink-1)', border: '1px solid var(--line-soft)', borderRadius: 16, overflow: 'hidden' }}>
      <div style={{
        padding: '13px 20px', borderBottom: '1px solid var(--line-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div className="mono" style={{ fontSize: 11, color: 'var(--fg-dim)', letterSpacing: '0.14em' }}>
          NATIONAL · CHEAPEST RIGHT NOW
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--green)' }}>
          <span className="pulse-dot" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--green)' }}/>
          LIVE
        </div>
      </div>

      {loading && [...Array(8)].map((_, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '1.1fr 0.9fr 0.9fr auto', gap: 12,
          padding: '11px 20px', alignItems: 'center',
          borderBottom: i < 7 ? '1px solid var(--line-soft)' : 'none',
        }}>
          <div style={{ height: 12, borderRadius: 4, background: 'var(--line-soft)', width: '75%' }} />
          <div style={{ height: 12, borderRadius: 4, background: 'var(--line-soft)', width: '60%' }} />
          <div style={{ height: 12, borderRadius: 4, background: 'var(--line-soft)', width: '55%' }} />
          <div style={{ height: 12, borderRadius: 4, background: 'var(--line-soft)', width: 40 }} />
        </div>
      ))}

      {!loading && rows.map((r, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '1.1fr 0.9fr 0.9fr auto', gap: 12,
          padding: '11px 20px', alignItems: 'center',
          borderBottom: i < rows.length - 1 ? '1px solid var(--line-soft)' : 'none',
          fontSize: 13.5,
        }}>
          <span style={{ color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.city} · {r.area}
          </span>
          <span style={{ color: 'var(--fg-dim)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.fuelName}
          </span>
          <span style={{ color: 'var(--fg-mute)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.brand}
          </span>
          <span className="mono" style={{ color: 'var(--green)', fontWeight: 600 }}>
            £{(r.pricePence / 100).toFixed(3)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Footer ─────────────────────────────────────────────────────────────────────

function LandingFooter({ onLegalOpen }: { onLegalOpen: (type: LegalModalType) => void }) {
  return (
    <footer style={{ background: 'var(--ink-0)', borderTop: '1px solid var(--line-soft)', padding: '40px 18px 24px' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10" style={{ marginBottom: 32 }}>
          <div>
            <Logo />
            <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-mute)', margin: '14px 0 0', maxWidth: 300 }}>
              Find the best fuel prices across the UK. Compare stations by total cost — including the journey to get there.
            </p>
          </div>
          <FooterCol title="Product" hrefs={[
            { label: 'Search',       href: '#'     },
            { label: 'How it works', href: '#how'  },
            { label: 'Data',         href: '#data' },
          ]} />
          <FooterCol title="Legal" actions={[
            { label: 'Terms of Use',    onClick: () => onLegalOpen('terms')      },
            { label: 'Privacy Policy',  onClick: () => onLegalOpen('privacy')    },
            { label: 'Data Disclaimer', onClick: () => onLegalOpen('disclaimer') },
          ]} />
          <FooterCol title="Contact" hrefs={[
            { label: 'fuelhunt@hotmail.com', href: 'mailto:fuelhunt@hotmail.com' },
            { label: 'Report an issue',      href: 'mailto:fuelhunt@hotmail.com' },
          ]} />
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 20, borderTop: '1px solid var(--line-soft)',
          fontSize: 12.5, color: 'var(--fg-dim)',
          flexWrap: 'wrap', gap: 8,
        }}>
          <span>© 2026 FuelHunt · Helping UK drivers save money on fuel.</span>
          <span className="mono">Data from official retailer APIs · No registration required</span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items, hrefs, actions }: {
  title: string;
  items?: string[];
  hrefs?: { label: string; href: string }[];
  actions?: { label: string; onClick: () => void }[];
}) {
  const linkStyle: React.CSSProperties = { fontSize: 13.5, color: 'var(--fg-mute)', textDecoration: 'none' };
  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--fg-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14, fontWeight: 600 }}>{title}</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {hrefs?.map((h, k) => (
          <li key={k}><a href={h.href} style={linkStyle}>{h.label}</a></li>
        ))}
        {actions?.map((a, k) => (
          <li key={k}>
            <button onClick={a.onClick} style={{ ...linkStyle, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {a.label}
            </button>
          </li>
        ))}
        {items?.map((item, k) => (
          <li key={k} style={{ fontSize: 13.5, color: 'var(--fg-mute)' }}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

// ── Main landing page ──────────────────────────────────────────────────────────

interface LandingPageProps {
  onLocationSelect: (location: Location, displayName: string) => void;
  onUseCurrentLocation: () => void;
  isLoadingCurrentLocation: boolean;
  onAboutModalOpen: () => void;
  totalStations?: number;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onLocationSelect,
  onUseCurrentLocation,
  isLoadingCurrentLocation,
  onAboutModalOpen,
  totalStations,
}) => {
  const [legalModal, setLegalModal] = useState<LegalModalType | null>(null);

  const stationCount = totalStations
    ? `${(totalStations / 1000).toFixed(1)}k+`
    : '4.4k+';

  return (
    <div style={{ background: 'var(--ink-0)', minHeight: '100vh', color: 'var(--fg)' }}>

      {/* ── TopBar ── */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '0 18px', height: 60,
        background: 'var(--ink-1)',
        borderBottom: '1px solid var(--line-soft)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <Logo />
        <div style={{ flex: 1 }} />
        <nav className="hidden sm:flex" style={{ alignItems: 'center', gap: 22 }}>
          <a href="#how" style={{ fontSize: 13.5, color: 'var(--fg-mute)', textDecoration: 'none' }}>How it works</a>
          <a href="#data" style={{ fontSize: 13.5, color: 'var(--fg-mute)', textDecoration: 'none' }}>Data</a>
          <button onClick={onAboutModalOpen} style={{ fontSize: 13.5, color: 'var(--fg-mute)', background: 'none', border: 'none', cursor: 'pointer' }}>About</button>
        </nav>
        <button
          onClick={onUseCurrentLocation}
          disabled={isLoadingCurrentLocation}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            height: 38, padding: '0 14px',
            background: 'var(--amber)', color: '#1a1d24',
            fontWeight: 600, fontSize: 13, borderRadius: 10, border: 'none',
            boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px rgba(245,165,36,0.25)',
            cursor: 'pointer', marginLeft: 8,
            opacity: isLoadingCurrentLocation ? 0.6 : 1,
          }}
        >
          <CrosshairIcon size={14} />
          <span className="hidden sm:inline">{isLoadingCurrentLocation ? 'Locating…' : 'Use my location'}</span>
          <span className="sm:hidden">{isLoadingCurrentLocation ? '…' : 'Use location'}</span>
        </button>
      </header>

      {/* ── Hero ── */}
      <section style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid var(--line-soft)' }}>
        {/* Ambient grid */}
        <div className="grid-bg" style={{ position: 'absolute', inset: 0, opacity: 0.6 }} />
        {/* Amber glow right */}
        <div style={{
          position: 'absolute', left: '55%', top: '-20%', width: 720, height: 720,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,165,36,0.18), rgba(245,165,36,0) 60%)',
          pointerEvents: 'none',
        }} />
        {/* Green glow left-bottom */}
        <div style={{
          position: 'absolute', left: '-10%', bottom: '-30%', width: 600, height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(34,211,154,0.08), transparent 60%)',
          pointerEvents: 'none',
        }} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" style={{
          position: 'relative',
          maxWidth: 1240, margin: '0 auto', padding: '48px 18px 56px',
          alignItems: 'center',
        }}>

          {/* Left: copy + search */}
          <div className="fade-up">
            {/* Live badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 12px 6px 8px',
              background: 'var(--amber-soft)', border: '1px solid rgba(245,165,36,0.25)',
              borderRadius: 999, fontSize: 12, color: '#fbbf3c', letterSpacing: '0.02em',
            }}>
              <span className="pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)' }} />
              LIVE · {stationCount} stations · refreshed daily
            </div>

            <h1 className="serif" style={{
              fontSize: 'clamp(48px, 6vw, 76px)', lineHeight: 1.0,
              letterSpacing: '-0.02em', margin: '26px 0 18px', fontWeight: 400,
            }}>
              The cheapest fuel<br />
              <em style={{ color: 'var(--amber)', fontStyle: 'italic' }}>that's actually</em><br />
              the cheapest.
            </h1>

            <p style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--fg-mute)', maxWidth: 520, margin: '0 0 28px' }}>
              Most apps rank stations by pump price. We rank by{' '}
              <span style={{ color: 'var(--fg)' }}>total cost</span> — fuel{' '}
              <em>plus</em> what it takes to drive there and back. Sometimes
              the 4p saving isn't worth the 6-mile detour.
            </p>

            {/* Search box */}
            <div style={{ maxWidth: 540, marginBottom: 14 }}>
              <LocationSearch
                onLocationSelect={onLocationSelect}
                onUseCurrentLocation={onUseCurrentLocation}
                isLoadingCurrentLocation={isLoadingCurrentLocation}
                placeholder="Postcode, town or city — e.g. SW1A 1AA"
              />
            </div>

            <button
              onClick={onUseCurrentLocation}
              disabled={isLoadingCurrentLocation}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 13.5, color: 'var(--fg-mute)', background: 'none',
                border: 'none', cursor: 'pointer', padding: '6px 0',
              }}
            >
              <CrosshairIcon size={14} />
              Or use my current location
              <span style={{ color: 'var(--fg-faint)' }}>·</span>
              <span style={{ color: 'var(--fg-faint)' }}>no account, no tracking</span>
            </button>

            {/* Trust stats */}
            <div style={{ marginTop: 44, display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
              {([
                [stationCount, 'stations'],
                ['14', 'retailers'],
                ['2×', 'daily refresh'],
                ['£0', 'ever'],
              ] as [string, string][]).map(([n, l], i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="serif" style={{ fontSize: 30, color: 'var(--fg)', letterSpacing: '-0.02em' }}>{n}</span>
                  <span style={{ fontSize: 12, color: 'var(--fg-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: live results preview */}
          <div className="fade-up hidden lg:block" style={{ animationDelay: '120ms' }}>
            <LiveResultsPanel />
          </div>
        </div>

        {/* Brand marquee */}
        <div style={{
          position: 'relative', borderTop: '1px solid var(--line-soft)',
          padding: '20px 0', overflow: 'hidden',
          maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
        }}>
          <div className="marquee-track">
            {[0, 1].map((dup) => (
              <div key={dup} style={{ display: 'flex', gap: 52, alignItems: 'center', paddingRight: 52 }}>
                {['BP', 'Shell', 'Tesco', "Sainsbury's", 'Asda', 'Morrisons', 'Costco', 'Esso', 'Texaco', 'Jet', 'Applegreen', 'Murco', 'Gulf', 'Harvest'].map((b, i) => (
                  <span key={i} style={{
                    fontSize: 16, fontWeight: 600, color: 'var(--fg-faint)',
                    letterSpacing: '0.04em', whiteSpace: 'nowrap',
                  }}>{b}</span>
                ))}
              </div>
            ))}
          </div>
          <span style={{
            position: 'absolute', left: 28, top: '50%', transform: 'translateY(-50%)',
            fontSize: 11, color: 'var(--fg-faint)',
            background: 'var(--ink-0)', paddingRight: 14,
            textTransform: 'uppercase', letterSpacing: '0.1em', zIndex: 1,
          }}>
            Live data from →
          </span>
        </div>
      </section>

      {/* ── Why total cost matters ── */}
      <section id="how" className="py-12 lg:py-24" style={{ padding: undefined, borderBottom: '1px solid var(--line-soft)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 18px' }}>
          <Eyebrow>Why total cost matters</Eyebrow>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-[72px]" style={{ marginTop: 24, alignItems: 'start' }}>
            <div>
              <h2 className="serif" style={{
                fontSize: 'clamp(36px, 4vw, 54px)', lineHeight: 1.05,
                letterSpacing: '-0.02em', margin: '0 0 24px', fontWeight: 400,
              }}>
                The cheapest pump<br />
                <em style={{ color: 'var(--amber)' }}>isn't always</em> the cheapest fill.
              </h2>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--fg-mute)', maxWidth: 480 }}>
                A 4p-per-litre saving on a 50-litre tank is £2. That's about 4 miles of driving in a
                typical car. If the cheaper station is 6 miles away, you've already lost.
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--fg-mute)', maxWidth: 480, marginTop: 16 }}>
                FuelHunt does the maths automatically — using your fuel type, fill amount,
                vehicle MPG, and the actual driving distance.
              </p>
            </div>
            <CompareReceipt />
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-12 lg:py-24" style={{ padding: undefined, borderBottom: '1px solid var(--line-soft)', background: 'var(--ink-1)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 18px' }}>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="serif" style={{
            fontSize: 'clamp(28px, 3.5vw, 46px)', lineHeight: 1.05,
            letterSpacing: '-0.02em', margin: '16px 0 36px', fontWeight: 400, maxWidth: 680,
          }}>
            Four steps. No account. No tracking.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4" style={{
            gap: 1, background: 'var(--line-soft)', border: '1px solid var(--line-soft)',
          }}>
            {[
              { n: '01', t: 'Set your location',  d: 'Postcode, town name, or one-tap GPS. Nothing stored.' },
              { n: '02', t: 'Tune your search',   d: 'Fuel type, fill amount, MPG, and search radius.' },
              { n: '03', t: 'See live prices',    d: 'Live data from 14 UK retailers, refreshed twice daily.' },
              { n: '04', t: 'Factor your trip',   d: 'Total cost = fuel price + drive there and back. Sorted.' },
            ].map((s) => (
              <div key={s.n} style={{ background: 'var(--ink-1)', padding: '28px 24px' }}>
                <div className="mono" style={{ fontSize: 12, color: 'var(--amber)', letterSpacing: '0.1em' }}>
                  STEP {s.n}
                </div>
                <h3 style={{ fontSize: 18, margin: '10px 0 8px', fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--fg)' }}>{s.t}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--fg-mute)', margin: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The data ── */}
      <section id="data" className="py-12 lg:py-24" style={{ padding: undefined, borderBottom: '1px solid var(--line-soft)' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-[72px]" style={{ maxWidth: 1240, margin: '0 auto', padding: '0 18px' }}>
          <div>
            <Eyebrow>The data</Eyebrow>
            <h2 className="serif" style={{
              fontSize: 'clamp(32px, 3.5vw, 46px)', lineHeight: 1.05,
              letterSpacing: '-0.02em', margin: '16px 0 22px', fontWeight: 400,
            }}>
              Straight from the source.
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--fg-mute)', maxWidth: 480 }}>
              We pull prices from official retailer feeds — the same data UK fuel retailers have
              been required to publish since November 2023. No scraping. No estimates. No paid placement.
            </p>
            <div style={{
              marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
              background: 'var(--line-soft)', border: '1px solid var(--line-soft)',
            }}>
              {([
                ['14',    'UK retailers'],
                [stationCount, 'stations'],
                ['~12 hr', 'max stale data'],
                ['2023',  'mandatory since'],
              ] as [string, string][]).map(([n, l], i) => (
                <div key={i} style={{ background: 'var(--ink-1)', padding: '20px 22px' }}>
                  <div className="serif" style={{ fontSize: 34, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--fg)' }}>{n}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 5 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <PriceTicker />
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-16 lg:py-28" style={{ padding: undefined }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative', padding: '0 18px' }}>
          <h2 className="serif" style={{
            fontSize: 'clamp(48px, 6vw, 78px)', lineHeight: 1.0,
            letterSpacing: '-0.025em', margin: 0, fontWeight: 400,
          }}>
            Stop overpaying<br />
            <em style={{ color: 'var(--amber)' }}>at the pump.</em>
          </h2>
          <p style={{ fontSize: 17, color: 'var(--fg-mute)', margin: '26px auto 34px', maxWidth: 520 }}>
            UK drivers using FuelHunt save by finding the truly cheapest fill — not just the lowest pump price.
            Free, forever, with no account.
          </p>
          <button
            onClick={onUseCurrentLocation}
            disabled={isLoadingCurrentLocation}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '16px 28px', borderRadius: 12, border: 'none',
              background: 'var(--amber)', color: '#1a1d24',
              fontWeight: 600, fontSize: 16, cursor: 'pointer',
              boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 12px 32px rgba(245,165,36,0.30)',
              opacity: isLoadingCurrentLocation ? 0.6 : 1,
            }}
          >
            Find cheap fuel near me <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <LandingFooter onLegalOpen={setLegalModal} />

      <LegalModal
        isOpen={legalModal !== null}
        type={legalModal ?? 'terms'}
        onClose={() => setLegalModal(null)}
      />
    </div>
  );
};

export default LandingPage;
